import { useEffect, useRef, useState } from "react"

import { partnersApi } from "@shared/apis"
import { useLayerPopupContext, useSchedule } from "@shared/contexts"

const DEFAULT_PREVIEW_TIME = 3
const MINUTE = 60 * 1000

interface Options {
  /** 미리보기 대상이 아닐 때(로그인 사용자 등) true */
  disabled?: boolean
}

/**
 * 공개방송 미리보기 제어.
 *
 * 파트너별 설정 시간이 지나면 방송을 막고 PREVIEW_LOGIN 팝업을 띄운다.
 * 팝업을 닫으면(로그인하기 / 닫기 / 딤 클릭) 방송을 재개하고 타이머를 처음부터 다시 돌린다.
 */
export default function usePreviewPopup({ disabled = false }: Options = {}) {
  const { schedule } = useSchedule()
  const {
    open,
    active,
    previewLogin: { handlePreviewLogin },
  } = useLayerPopupContext()

  const [previewRound, setPreviewRound] = useState<number>(0)
  const [isPreviewBlocked, setIsPreviewBlocked] = useState<boolean>(false)
  const isPopupShownRef = useRef<boolean>(false)

  const partnerId = schedule?.user.partner?.id
  const previewTime = schedule?.user.partner?.previewTime

  // 미리보기 제한을 적용할 대상인지
  const isPreviewTarget = !disabled

  // 미리보기 타이머. previewRound 가 올라가면 처음부터 다시 시작된다
  useEffect(() => {
    if (!partnerId || !isPreviewTarget) {
      return
    }

    const previewDelay = (previewTime || DEFAULT_PREVIEW_TIME) * MINUTE

    let timer: NodeJS.Timeout
    let isCanceled = false

    partnersApi.get(partnerId).then(({ data }) => {
      if (!data.isUsePreview || isCanceled) {
        return
      }

      timer = setTimeout(() => {
        setIsPreviewBlocked(true)

        // 파트너별 팝업 문구/버튼 문구. 없으면 팝업에서 기본 문구를 사용한다
        handlePreviewLogin({
          message: data.popupMessage || "",
          buttonText: data.popupButtonText || "",
        })

        open("PREVIEW_LOGIN")
      }, previewDelay)
    })

    return () => {
      isCanceled = true
      clearTimeout(timer)
    }
  }, [
    partnerId,
    previewTime,
    isPreviewTarget,
    previewRound,
    open,
    handlePreviewLogin,
  ])

  // 팝업이 닫히면 방송을 재개하고 타이머를 다시 돌린다.
  // ref 가드가 없으면 팝업이 뜨기 전(active 가 아직 undefined 일 때) 닫힘으로 오인한다.
  useEffect(() => {
    if (active === "PREVIEW_LOGIN") {
      isPopupShownRef.current = true
      return
    }

    if (isPopupShownRef.current) {
      isPopupShownRef.current = false
      setIsPreviewBlocked(false)
      setPreviewRound((round) => round + 1)
    }
  }, [active])

  // 시청 중 로그인 등으로 대상에서 벗어나면 차단을 푼다
  useEffect(() => {
    if (isPreviewTarget) return

    isPopupShownRef.current = false
    setIsPreviewBlocked(false)
  }, [isPreviewTarget])

  return { isPreviewBlocked }
}
